/**
 * Shared Sentry environment detection logic.
 * Used by server, edge, and client Sentry configs.
 */

type SentryEnvResult = {
  isDevelopment: boolean;
  environment: string;
  shouldInitialize: boolean;
};

/**
 * Detects the current environment for Sentry initialization (server-side only).
 * Checks SENTRY_ENVIRONMENT first, then known deployment hostnames,
 * then VERCEL_ENV, then NODE_ENV.
 *
 * NOTE: This function should only be called from server-side code.
 * It accesses process.env directly to avoid importing the validated env schema
 * which would cause issues in edge runtime.
 *
 * Staging should set SENTRY_ENVIRONMENT=staging in Vercel, but we also infer
 * it from VERCEL_URL to prevent staging custom-domain traffic from being
 * grouped under production.
 */
export function getSentryEnvironment(): SentryEnvResult {
  const sentryEnvironment = process.env.SENTRY_ENVIRONMENT;
  const vercelEnv = process.env.VERCEL_ENV;
  const vercelUrl = process.env.VERCEL_URL;
  const nodeEnv = process.env.NODE_ENV;
  const isStagingDeployment = vercelUrl === 'staging.arkyv.ai';

  const isDevelopment =
    nodeEnv === 'development' ||
    vercelEnv === 'development' ||
    sentryEnvironment === 'development' ||
    (!vercelUrl && !sentryEnvironment);

  const environment =
    sentryEnvironment ||
    (isDevelopment
      ? 'development'
      : isStagingDeployment
        ? 'staging'
        : vercelEnv || nodeEnv || 'production');

  return {
    isDevelopment,
    environment,
    shouldInitialize: environment === 'production' || environment === 'staging',
  };
}

/**
 * Client-side environment detection.
 * Uses only browser-available information (hostname, protocol).
 * Does NOT access server-side environment variables.
 */
export function getSentryClientEnvironment(): SentryEnvResult {
  // Check if we're on localhost or a development URL
  const isLocalhost =
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' ||
      window.location.hostname === '127.0.0.1');

  // Check for staging deployment (staging.arkyv.ai)
  const isStaging =
    typeof window !== 'undefined' &&
    window.location.hostname === 'staging.arkyv.ai';

  // Check for Vercel preview deployments (*.vercel.app but not staging or production)
  const isPreview =
    typeof window !== 'undefined' &&
    window.location.hostname.endsWith('.vercel.app');

  // Production is app.arkyv.ai
  const isProduction =
    typeof window !== 'undefined' &&
    window.location.hostname === 'app.arkyv.ai';

  const isDevelopment = isLocalhost;

  let environment: string;
  if (isProduction) {
    environment = 'production';
  } else if (isStaging) {
    environment = 'staging';
  } else if (isPreview) {
    environment = 'preview';
  } else {
    environment = 'development';
  }

  return {
    isDevelopment,
    environment,
    shouldInitialize: isProduction || isStaging,
  };
}

export const SENTRY_DSN =
  'https://db2829ed90b40f8417bbe5756d410ba2@o4510074339852288.ingest.de.sentry.io/4510074341687376';
